/**
 * VendorPayoutService
 * Service for managing vendor payouts
 */
import { CrudService } from '../base/CrudService';
/**
 * Service for vendor payout CRUD operations with vendor-specific queries
 */
export class VendorPayoutService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorPayoutService.DEFAULT_SERVICE_PATH,
            basePath: VendorPayoutService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get all payout records for a specific vendor
     * @param vendorId - The vendor ID
     * @returns Array of vendor payouts
     */
    async getByVendor(vendorId) {
        const response = await this.getByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get all payout records for a specific vendor with full response
     * @param vendorId - The vendor ID
     * @returns ResponseData with array of vendor payouts
     */
    getByVendorRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}`);
        return this.http.get(url);
    }
    /**
     * Get all pending payouts
     * @returns Array of pending vendor payouts
     */
    async getPending() {
        const response = await this.getPendingRaw();
        return response.data ?? [];
    }
    /**
     * Get all pending payouts with full response
     * @returns ResponseData with array of pending vendor payouts
     */
    getPendingRaw() {
        const url = this.buildUrl('pending');
        return this.http.get(url);
    }
    /**
     * Process a payout for a vendor
     * @param vendorId - The vendor ID
     * @returns The processed payout
     */
    async processPayout(vendorId) {
        const response = await this.processPayoutRaw(vendorId);
        if (!response.data) {
            throw new Error(`Failed to process payout for vendor: vendorId=${vendorId}`);
        }
        return response.data;
    }
    /**
     * Process a payout for a vendor with full response
     * @param vendorId - The vendor ID
     * @returns ResponseData with the processed payout
     */
    processPayoutRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}/process`);
        return this.http.post(url, {});
    }
    /**
     * Update the status of a payout
     * @param payoutId - The payout ID
     * @param statusId - The new status ID
     * @returns The updated payout
     */
    async updateStatus(payoutId, statusId) {
        const response = await this.updateStatusRaw(payoutId, statusId);
        if (!response.data) {
            throw new Error(`Failed to update payout status: payoutId=${payoutId}`);
        }
        return response.data;
    }
    /**
     * Update the status of a payout with full response
     * @param payoutId - The payout ID
     * @param statusId - The new status ID
     * @returns ResponseData with the updated payout
     */
    updateStatusRaw(payoutId, statusId) {
        const url = this.buildUrl(`${payoutId}/status`);
        return this.http.put(url, { statusId });
    }
    // ==================== MODEL VIEW QUERIES ====================
    /**
     * Get all vendor payouts as model views
     * @returns Array of vendor payout views
     */
    async getViews() {
        const response = await this.getViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all vendor payouts as model views with full response
     * @returns ResponseData with array of vendor payout views
     */
    getViewsRaw() {
        const url = this.buildUrl('views');
        return this.http.get(url);
    }
    /**
     * Get vendor payout model view by ID
     * @param id - Payout ID
     * @returns The vendor payout view
     */
    async getView(id) {
        const response = await this.getViewRaw(id);
        if (!response.data) {
            throw new Error(`VendorPayout view not found: id=${id}`);
        }
        return response.data;
    }
    /**
     * Get vendor payout model view by ID with full response
     * @param id - Payout ID
     * @returns ResponseData with vendor payout view
     */
    getViewRaw(id) {
        const url = this.buildUrl(`${id}/view`);
        return this.http.get(url);
    }
    /**
     * Get all payout model views for a specific vendor
     * @param vendorId - Vendor ID
     * @returns Array of vendor payout views
     */
    async getViewsByVendor(vendorId) {
        const response = await this.getViewsByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get all payout model views for a specific vendor with full response
     * @param vendorId - Vendor ID
     * @returns ResponseData with array of vendor payout views
     */
    getViewsByVendorRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}/views`);
        return this.http.get(url);
    }
}
VendorPayoutService.DEFAULT_SERVICE_PATH = 'vendors';
VendorPayoutService.BASE_PATH = 'api/vendor-payouts';
//# sourceMappingURL=VendorPayoutService.js.map